import {
  Box,
  Button,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Stack,
  Text,
  SimpleGrid,
  Input,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ControllableDrawer } from 'components/Drawer';
import { FormikInput, FormikTextArea, FormikCheckbox } from 'components/Formik';
import AssignEmployeesAndGroupsField from 'containers/JobsTasks/RightDrawer/AssignEmployeesAndGroupsField';

import { initialJobFormData } from '../schema';

const JobFormDetails: React.FC<unknown> = () => {
  const { t } = useTranslation(['jobsTasks', 'common']);

  const { values } = useFormikContext<typeof initialJobFormData>();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  return (
    <Box>
      <ControllableDrawer onClose={onDrawerClose} isOpen={isDrawerOpen}>
        <DrawerContent>
          <DrawerCloseButton />
          <AssignEmployeesAndGroupsField />
        </DrawerContent>
      </ControllableDrawer>
      <Stack>
        <Text fontSize="13px" textTransform="uppercase" fontWeight="600" color="gray.500" mb="4">
          {t('jobsTasks:jobDetails')}
        </Text>
        <SimpleGrid columns={{ sm: 2 }} spacingX={{ sm: 4 }}>
          <FormikInput type="text" name="name" label={t('jobsTasks:jobName')} formControlProps={{ isRequired: true }} />
          <FormikInput type="text" name="job_code" label={t('jobsTasks:jobCode')} />
          <Box mb="6">
            <Text color="gray.500" fontSize="12px" fontWeight="600" mb="1" textTransform="uppercase">
              {t('jobsTasks:assignToUserOrGroup')}
            </Text>
            <InputGroup size="lg">
              <Input
                fontSize="sm"
                variant="filled"
                type="text"
                border="1px"
                borderColor="inherit"
                onChange={() => null}
                value={`${values.users.length || t('common:all')} ${
                  values.users.length === 1 ? t('common:user') : t('common:users')
                }`}
              />
              <InputRightElement
                width="auto"
                mr="2"
                children={
                  <Button size="sm" variant="link" onClick={onDrawerOpen}>
                    {values.users.length ? t('common:edit') : t('common:add')}
                  </Button>
                }
              />
            </InputGroup>
          </Box>
          <Box>
            <FormikInput
              type="text"
              name="rate"
              label={
                <>
                  {t('jobsTasks:hourlyRate')}{' '}
                  <Text as="span" textTransform="lowercase">
                    ({t('jobsTasks:overridesUserRate')})
                  </Text>
                </>
              }
              formControlProps={{ mb: 2 }}
            />
            <FormikCheckbox
              options={[{ label: t('common:trackMileage'), value: 'yes' }]}
              name="track_mileage"
              checkboxProps={{ colorScheme: 'secondary' }}
            />
          </Box>
          <FormikTextArea name="description" label={t('common:description')} />
        </SimpleGrid>
        <Text fontSize="13px" textTransform="uppercase" fontWeight="600" color="gray.500" mb="4">
          {t('jobsTasks:contactDetails')}
        </Text>
        <FormikCheckbox
          options={[{ label: t('jobsTasks:addContactInformation'), value: 'yes' }]}
          name="contactRequired"
          checkboxProps={{ colorScheme: 'secondary' }}
        />
        {values.contactRequired.includes('yes') ? (
          <SimpleGrid columns={{ sm: 2 }} spacingX={{ sm: 4 }}>
            <FormikInput type="text" name="contact_name" label={t('jobsTasks:contactName')} />
            <FormikInput type="text" name="contact_email" label={t('jobsTasks:contactEmail')} />
            <FormikInput type="text" name="contact_phone" label={t('jobsTasks:contactPhone')} />
            <FormikInput type="text" name="contact_mobile" label={t('jobsTasks:contactMobile')} />
          </SimpleGrid>
        ) : null}
      </Stack>
    </Box>
  );
};

export default JobFormDetails;
